'use client';

import {useForwardingRef} from '@rocket-ui/react-tools';
import React, {useCallback, useLayoutEffect, useRef} from 'react';
import {cursor} from '../tools';
import {InputProps} from '../types';

type OnChangeHandler = InputProps['onChange'];
type OnInputHandler = InputProps['onInput'];

type UseSuffixProps = Omit<InputProps, 'ref'> & {
	ref?: React.Ref<HTMLInputElement>;
	suffix: string;
};

export function useSuffix({
	ref: inputRef,
	value: inputValue,
	suffix,
	onChange,
	onInput,
	placeholder: inputPlaceholder,
	dynamicPlaceholder: inputDynamicPlaceholder,
	onFocus,
	onBlur,
	...props
}: UseSuffixProps) {
	const focused = useRef(false);
	const ref = useForwardingRef(inputRef);

	const isEmpty = (inputValue ?? '') === '';
	const value = isEmpty ? '' : `${inputValue ?? ''} ${suffix}`;

	const placeholder = useCallback(() => {
		const pl = typeof inputPlaceholder === 'function' ? inputPlaceholder({value: inputValue}) : inputPlaceholder;
		return (inputValue ?? '') === '' && pl ? pl : undefined;
	}, [inputPlaceholder, inputValue]);

	const dynamicPlaceholder = useCallback(
		() =>
			inputDynamicPlaceholder
				? typeof inputDynamicPlaceholder === 'function'
					? inputDynamicPlaceholder({value: inputValue})
					: `${
							inputValue ? inputDynamicPlaceholder.slice(0, -String(inputValue).length) : inputDynamicPlaceholder
					  }${value}`
				: undefined,
		[inputValue, value, inputDynamicPlaceholder],
	);

	const handleChange: OnChangeHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			event.target.value = event.target.value.trim().replace(suffix, '').trim();

			onChange?.(event);
		},
		[onChange, suffix],
	);

	const handleFocus = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			focused.current = true;
			onFocus?.(event);
		},
		[onFocus],
	);

	const handleBlur = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			focused.current = false;
			onBlur?.(event);
		},
		[onBlur],
	);

	const setCursorPosition = useCallback(
		(value?: string | number) => {
			const inputElement = ref.current;
			if (!inputElement || !focused.current) return;

			const valueStr = value !== undefined ? String(value) : inputElement.value;
			const position = Math.max(0, valueStr.length - suffix.length - 1);

			cursor.setPosition(inputElement, position);
		},
		[ref, suffix.length],
	);

	const handleInput: OnInputHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onInput?.(event);
			setTimeout(setCursorPosition, 0);
		},
		[onInput, setCursorPosition],
	);

	useLayoutEffect(() => {
		setCursorPosition(value);
	}, [setCursorPosition, value]);

	return {
		...props,
		ref,
		value,
		onChange: handleChange,
		onFocus: handleFocus,
		onBlur: handleBlur,
		onInput: handleInput,
		placeholder,
		dynamicPlaceholder,
	};
}
